/**
 * BabyCareCharts
 *
 * Individual charts for feeding, diapers, sleep, and temperature.
 * Uses recharts library with dark glass card styling.
 */

import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';
import {
  Baby,
  Droplets,
  Moon,
  Thermometer,
} from 'lucide-react';
import { BabyCareEntry } from '@/services/babyCareService';

interface BabyCareChartsProps {
  entries: BabyCareEntry[];
  selectedDays: number;
  customStartDate?: string;
}

// Colors for feeding types (FeedingType: 'breast' | 'bottle' | 'solid')
const FEEDING_COLORS = {
  breast: '#ec4899', // pink-500
  bottle: '#f472b6', // pink-400
  solid: '#fb7185', // rose-400
};

// Colors for diaper types
const DIAPER_COLORS = {
  wet: '#fbbf24', // amber-400
  dirty: '#f59e0b', // amber-500
  both: '#d97706', // amber-600
  dry: '#fcd34d', // amber-300
};

// Sleep color (from icon)
const SLEEP_COLOR = '#818cf8'; // indigo-400

// Temperature color (from icon)
const TEMP_COLOR = '#34d399'; // emerald-400

// Helper to get date key for grouping
function getDateKey(date: Date, selectedDays: number): string {
  if (selectedDays === 1) {
    return `${String(date.getHours()).padStart(2, '0')}:00`;
  }
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

// Generate all time slots for the selected period
function generateTimeSlots(selectedDays: number, customStartDate?: string): string[] {
  if (selectedDays === 1) {
    return Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);
  }

  const slots: string[] = [];
  const start = customStartDate ? new Date(customStartDate) : new Date();
  if (!customStartDate) {
    start.setDate(start.getDate() - selectedDays + 1);
  }
  start.setHours(0, 0, 0, 0);

  for (let i = 0; i < selectedDays; i++) {
    const date = new Date(start);
    date.setDate(date.getDate() + i);
    slots.push(date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }));
  }
  return slots;
}

// Custom tooltip style
const tooltipStyle = {
  backgroundColor: 'rgba(29, 53, 65, 0.95)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '10px',
  color: 'white',
};

export function BabyCareCharts({ entries, selectedDays, customStartDate }: BabyCareChartsProps) {
  const timeSlots = useMemo(
    () => generateTimeSlots(selectedDays, customStartDate),
    [selectedDays, customStartDate]
  );

  // Prepare feeding data
  const feedingData = useMemo(() => {
    const feedingEntries = entries.filter(e => e.care_type === 'feeding');
    const grouped = new Map<string, { breast: number; bottle: number; solid: number }>();

    // Initialize all slots
    timeSlots.forEach(slot => {
      grouped.set(slot, { breast: 0, bottle: 0, solid: 0 });
    });

    feedingEntries.forEach(entry => {
      const dateKey = getDateKey(new Date(entry.recorded_at), selectedDays);
      const current = grouped.get(dateKey);
      if (current) {
        if (entry.feeding_type === 'breast') current.breast++;
        else if (entry.feeding_type === 'bottle') current.bottle++;
        else if (entry.feeding_type === 'solid') current.solid++;
      }
    });

    return timeSlots.map(slot => ({
      name: slot,
      ...grouped.get(slot),
    }));
  }, [entries, selectedDays, timeSlots]);

  // Prepare diaper data
  const diaperData = useMemo(() => {
    const diaperEntries = entries.filter(e => e.care_type === 'diaper');
    const grouped = new Map<string, { wet: number; dirty: number; both: number; dry: number }>();

    timeSlots.forEach(slot => {
      grouped.set(slot, { wet: 0, dirty: 0, both: 0, dry: 0 });
    });

    diaperEntries.forEach(entry => {
      const dateKey = getDateKey(new Date(entry.recorded_at), selectedDays);
      const current = grouped.get(dateKey);
      if (current) {
        if (entry.diaper_type === 'wet') current.wet++;
        else if (entry.diaper_type === 'dirty') current.dirty++;
        else if (entry.diaper_type === 'both') current.both++;
        else if (entry.diaper_type === 'dry') current.dry++;
      }
    });

    return timeSlots.map(slot => ({
      name: slot,
      ...grouped.get(slot),
    }));
  }, [entries, selectedDays, timeSlots]);

  // Prepare sleep data (total hours per day)
  const sleepData = useMemo(() => {
    const sleepEntries = entries.filter(e => e.care_type === 'sleep' && e.ended_at);
    const grouped = new Map<string, number>();

    timeSlots.forEach(slot => {
      grouped.set(slot, 0);
    });

    sleepEntries.forEach(entry => {
      const dateKey = getDateKey(new Date(entry.recorded_at), selectedDays);
      const current = grouped.get(dateKey) || 0;
      const start = new Date(entry.recorded_at).getTime();
      const end = new Date(entry.ended_at!).getTime();
      const hours = (end - start) / (1000 * 60 * 60);
      grouped.set(dateKey, current + hours);
    });

    return timeSlots.map(slot => ({
      name: slot,
      hours: Number((grouped.get(slot) || 0).toFixed(1)),
    }));
  }, [entries, selectedDays, timeSlots]);

  // Calculate sleep average
  const sleepAverage = useMemo(() => {
    const values = sleepData.filter(d => d.hours > 0).map(d => d.hours);
    if (values.length === 0) return 0;
    return Number((values.reduce((a, b) => a + b, 0) / values.length).toFixed(1));
  }, [sleepData]);

  // Prepare temperature data
  const temperatureData = useMemo(() => {
    const tempEntries = entries.filter(e => e.care_type === 'temperature' && e.temperature_value);
    const grouped = new Map<string, number[]>();

    timeSlots.forEach(slot => {
      grouped.set(slot, []);
    });

    tempEntries.forEach(entry => {
      const dateKey = getDateKey(new Date(entry.recorded_at), selectedDays);
      const current = grouped.get(dateKey) || [];
      current.push(entry.temperature_value!);
      grouped.set(dateKey, current);
    });

    return timeSlots.map(slot => {
      const temps = grouped.get(slot) || [];
      const avgTemp = temps.length > 0
        ? Number((temps.reduce((a, b) => a + b, 0) / temps.length).toFixed(1))
        : null;
      return {
        name: slot,
        temp: avgTemp,
      };
    });
  }, [entries, selectedDays, timeSlots]);

  // Calculate temperature average
  const tempAverage = useMemo(() => {
    const values = temperatureData.filter(d => d.temp !== null).map(d => d.temp as number);
    if (values.length === 0) return 0;
    return Number((values.reduce((a, b) => a + b, 0) / values.length).toFixed(1));
  }, [temperatureData]);

  // Check if there's data for each chart
  const hasFeedingData = entries.some(e => e.care_type === 'feeding');
  const hasDiaperData = entries.some(e => e.care_type === 'diaper');
  const hasSleepData = entries.some(e => e.care_type === 'sleep' && e.ended_at);
  const hasTempData = entries.some(e => e.care_type === 'temperature' && e.temperature_value);

  if (!hasFeedingData && !hasDiaperData && !hasSleepData && !hasTempData) {
    return null;
  }

  return (
    <div className="space-y-4 mt-6">
      {/* Feeding Chart */}
      {hasFeedingData && (
        <div className="glass-effect-dark rounded-[15px] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Baby className="w-5 h-5 text-pink-500" />
            <h3 className="text-white font-medium">Füttern</h3>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={feedingData}>
              <XAxis
                dataKey="name"
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                tickLine={false}
                interval={selectedDays === 1 ? 3 : 'preserveStartEnd'}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelStyle={{ color: 'white' }}
                cursor={{ fill: 'rgba(255,255,255,0.1)' }}
              />
              <Legend
                wrapperStyle={{ color: 'white', fontSize: 12 }}
                formatter={(value) => {
                  const labels: Record<string, string> = {
                    breast: 'Stillen',
                    bottle: 'Flasche',
                    solid: 'Brei',
                  };
                  return <span style={{ color: 'rgba(255,255,255,0.8)' }}>{labels[value] || value}</span>;
                }}
              />
              <Bar dataKey="breast" stackId="a" fill={FEEDING_COLORS.breast} radius={[0, 0, 0, 0]} />
              <Bar dataKey="bottle" stackId="a" fill={FEEDING_COLORS.bottle} radius={[0, 0, 0, 0]} />
              <Bar dataKey="solid" stackId="a" fill={FEEDING_COLORS.solid} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Diaper Chart */}
      {hasDiaperData && (
        <div className="glass-effect-dark rounded-[15px] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Droplets className="w-5 h-5 text-amber-500" />
            <h3 className="text-white font-medium">Windeln</h3>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={diaperData}>
              <XAxis
                dataKey="name"
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                tickLine={false}
                interval={selectedDays === 1 ? 3 : 'preserveStartEnd'}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelStyle={{ color: 'white' }}
                cursor={{ fill: 'rgba(255,255,255,0.1)' }}
              />
              <Legend
                wrapperStyle={{ color: 'white', fontSize: 12 }}
                formatter={(value) => {
                  const labels: Record<string, string> = {
                    wet: 'Nass',
                    dirty: 'Voll',
                    both: 'Beides',
                    dry: 'Trocken',
                  };
                  return <span style={{ color: 'rgba(255,255,255,0.8)' }}>{labels[value] || value}</span>;
                }}
              />
              <Bar dataKey="wet" stackId="a" fill={DIAPER_COLORS.wet} radius={[0, 0, 0, 0]} />
              <Bar dataKey="dirty" stackId="a" fill={DIAPER_COLORS.dirty} radius={[0, 0, 0, 0]} />
              <Bar dataKey="both" stackId="a" fill={DIAPER_COLORS.both} radius={[0, 0, 0, 0]} />
              <Bar dataKey="dry" stackId="a" fill={DIAPER_COLORS.dry} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Sleep Chart */}
      {hasSleepData && (
        <div className="glass-effect-dark rounded-[15px] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Moon className="w-5 h-5 text-indigo-400" />
            <h3 className="text-white font-medium">Schlafen</h3>
            {sleepAverage > 0 && (
              <span className="text-white/50 text-sm ml-auto">
                Ø {sleepAverage}h
              </span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={sleepData}>
              <XAxis
                dataKey="name"
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                tickLine={false}
                interval={selectedDays === 1 ? 3 : 'preserveStartEnd'}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                tickLine={false}
                domain={[0, 'auto']}
                unit="h"
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelStyle={{ color: 'white' }}
                formatter={(value) => [`${value}h`, 'Schlaf']}
              />
              {sleepAverage > 0 && (
                <ReferenceLine
                  y={sleepAverage}
                  stroke={SLEEP_COLOR}
                  strokeDasharray="5 5"
                  strokeOpacity={0.6}
                />
              )}
              <Line
                type="monotone"
                dataKey="hours"
                stroke={SLEEP_COLOR}
                strokeWidth={2}
                dot={{ fill: SLEEP_COLOR, strokeWidth: 0, r: 3 }}
                activeDot={{ fill: SLEEP_COLOR, strokeWidth: 0, r: 5 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Temperature Chart */}
      {hasTempData && (
        <div className="glass-effect-dark rounded-[15px] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Thermometer className="w-5 h-5 text-emerald-400" />
            <h3 className="text-white font-medium">Temperatur</h3>
            {tempAverage > 0 && (
              <span className="text-white/50 text-sm ml-auto">
                Ø {tempAverage}°C
              </span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={temperatureData}>
              <XAxis
                dataKey="name"
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                tickLine={false}
                interval={selectedDays === 1 ? 3 : 'preserveStartEnd'}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                tickLine={false}
                domain={[34, 41]}
                unit="°"
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelStyle={{ color: 'white' }}
                formatter={(value) => value !== null ? [`${value}°C`, 'Temperatur'] : ['-', 'Temperatur']}
              />
              {tempAverage > 0 && (
                <ReferenceLine
                  y={tempAverage}
                  stroke={TEMP_COLOR}
                  strokeDasharray="5 5"
                  strokeOpacity={0.6}
                />
              )}
              <Line
                type="monotone"
                dataKey="temp"
                stroke={TEMP_COLOR}
                strokeWidth={2}
                dot={{ fill: TEMP_COLOR, strokeWidth: 0, r: 3 }}
                activeDot={{ fill: TEMP_COLOR, strokeWidth: 0, r: 5 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
